import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';
import { F2120, pw2202Api } from '../../services/pw2202Api';
import { BatchAcceptanceBridge, pw2202f1AcceptanceManifest } from '../../features/acceptance';
import { usePW2202Mode } from './hooks/usePW2202Mode';
import { useLookupNames } from './hooks/useLookupNames';
import { usePW2202Record } from './hooks/usePW2202Record';
import { usePW2202Navigation } from './hooks/usePW2202Navigation';
import { useModulePermission } from '../../hooks/usePermission';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Detail from './components/Tab2Detail';
import Tab3AmountMemo from './components/Tab3AmountMemo';
import ActionBar from './components/ActionBar';
import { getStatusBadge, isApproved, dateToInput, inputToDate } from './helpers';
import './PW2202Form.css';

const TAB_LABELS = ['採購退回', '明細', '金額/備註'];
const NUMBER_TYPE_OPTIONS = [
  { value: ' ', label: '一般' },
  { value: 'M', label: 'M' },
  { value: 'P', label: 'P' },
];

const PW2202Form: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // Hooks
  const perms = useModulePermission(Modules.PW2202);
  const { mode, setMode, isEditing } = usePW2202Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();
  const {
    record, formData, details,
    selectedDetailRow, loading,
    setSelectedDetailRow, setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    setDetailField, recalcMasterTotals,
    revertToRecord, resetForAdd, applyResult,
  } = usePW2202Record();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      resolveAllNames(result.data);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = usePW2202Navigation(loadRecordWithNames);

  // Initial Load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load PW2202 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mode Actions [T-F07: dual-state buttons]
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      await handleSave(mode, async (result: F2120) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.f2120_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        resolveAllNames(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
      setActiveTab(0);
      clearNames();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, resolveAllNames, setMode, resetForAdd, clearNames]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      if (record) resolveAllNames(record);
      setMode('browse');
    } else {
      if (!record) return;
      // F2120_80='P' locks edit
      if (isApproved(record)) {
        message.warning('已核准的退回單無法修改');
        return;
      }
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, resolveAllNames, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    if (isApproved(record)) {
      message.warning('已核准的退回單無法刪除');
      return;
    }
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除退回單 ${record.f2120_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordWithNames(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => { navTo(idx, isEditing); }, [navTo, isEditing]);
  const handlePrint = useCallback(() => {
    if (!record || isEditing) return;
    message.info('列印功能待實作');
  }, [record, isEditing]);

  const statusBadge = getStatusBadge(record);

  return (
    <PermissionGuard module={Modules.PW2202} action="query">
    <div className="pw2202-form">
      {loading && <div className="loading-overlay">載入中...</div>}
      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <span className="current">採購退回單</span>
            </nav>
            <span className="title-separator">--</span>
            <h1>採購退回單 Purchase Return</h1>
            <span className="badge badge-default">PW2202</span>
            {statusBadge && <span className={`badge ${statusBadge.cls}`}>{statusBadge.text}</span>}
            {/* 核准狀態 label: visible when F2120_80='P' */}
            {record && (record.f2120_80 || '').trim() === 'P' && (
              <span style={{ color: '#389e0d', fontWeight: 600, fontSize: 13, marginLeft: 8 }}>已核准</span>
            )}
          </div>
        </div>

        {/* Search Bar + Master Header Fields */}
        <div className="search-bar">
          <label>退回單號</label>
          <input type="text" value={String(getField('f2120_01') || '')} disabled style={{ width: 120 }} data-contract-field="f2120_01" />
          <label>日期</label>
          <input type="date"
            value={dateToInput(getField('f2120_11') as string | null)}
            onChange={e => setField('f2120_11', inputToDate(e.target.value) as F2120[keyof F2120])}
            disabled={!isEditing} style={{ width: 140 }} data-contract-field="f2120_11" />
          <label>跳號別</label>
          <select
            value={String(getField('f2120_31') || '')}
            onChange={e => setField('f2120_31', e.target.value)}
            disabled={!isEditing}
            style={{ width: 80 }} data-contract-field="f2120_31"
          >
            {NUMBER_TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <label data-acceptance-non-field="record_navigation_query">快速查詢</label>
          <input
            type="text"
            placeholder="輸入退回單號"
            disabled={isEditing}
            style={{ width: 140 }}
            onKeyDown={async (e) => {
              if (e.key === 'Enter') {
                const val = (e.target as HTMLInputElement).value.trim();
                if (val) {
                  await loadRecordWithNames(val);
                  const idx = allIds.indexOf(val);
                  if (idx >= 0) setCurrentIdx(idx);
                }
              }
            }}
          />
          <span style={{ fontSize: 12, color: '#999' }}>
            {allIds.length > 0 ? `${currentIdx + 1} / ${allIds.length}` : '無資料'}
          </span>
        </div>

        {/* Tabs (3 pages) */}
        <div className="tabs">
          <div className="tab-nav">
            {TAB_LABELS.map((label, i) => (
              <button
                key={i}
                className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className={`tab-pane ${activeTab === 0 ? 'active' : ''}`}>
            <Tab1BasicInfo
              formData={formData} names={names} isEditing={isEditing}
              getField={getField} setField={setField}
              resolveSingleName={resolveSingleName} />
          </div>
          <div className={`tab-pane ${activeTab === 1 ? 'active' : ''}`}>
            <Tab2Detail
              formData={formData} details={details}
              selectedDetailRow={selectedDetailRow} isEditing={isEditing}
              getField={getField} setDetails={setDetails}
              setSelectedDetailRow={setSelectedDetailRow}
              addDetailRow={addDetailRow} deleteDetailRow={deleteDetailRow}
              setDetailField={setDetailField} />
          </div>
          <div className={`tab-pane ${activeTab === 2 ? 'active' : ''}`}>
            <Tab3AmountMemo
              formData={formData} isEditing={isEditing}
              getField={getField} setField={setField}
              recalcMasterTotals={recalcMasterTotals} />
          </div>
        </div>

        {/* Bottom Action Bar (Pwtcmd1) [T-F08] */}
        <ActionBar
          mode={mode} isEditing={isEditing} record={record}
          currentIdx={currentIdx} allIdsLength={allIds.length}
          permissions={perms}
          onAdd={handleAddAction} onModify={handleModifyAction}
          onDelete={handleDeleteAction} onPrint={handlePrint}
          onNavTo={handleNavTo} onExit={() => navigate('/erp')}
        />
        <BatchAcceptanceBridge
          manifest={pw2202f1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record?.f2120_01 ? [record.f2120_01] : []}
          keyOf={(item) => item.f2120_01}
          queryExact={(key) => pw2202Api.get(key)}
          lastApiResponse={record}
          loading={loading}
          onAutoFill={() => allIds[0] && handleNavTo(0)}
          autoFillDisabled={allIds.length === 0}
          screenDraft={{ master: formData, details }}
        />
      </div>
    </div>
    </PermissionGuard>
  );
};

export default PW2202Form;
